import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Surface, PageSection, formatINR } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Plus, Pencil, Trash2, UserCircle, Save, ArrowLeft, ChevronLeft, ChevronRight, ScrollText } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const F = ({ label, className = "", children }) => (
  <label className={`text-sm ${className}`}><span className="block text-[11px] uppercase tracking-widest text-gray-500 mb-1">{label}</span>{children}</label>
);

const EMPTY = {
  name: "", code: "", source: "hq_direct", branch_id: "", customer_type: "retail", price_level: 2,
  mobile: "", mobile2: "", email: "",
  address_line1: "", address_line2: "", city: "", district: "", state: "", pincode: "", country: "India",
  gstin: "", pan: "",
  opening_balance: 0, opening_balance_at: new Date().toISOString().slice(0, 10),
  notes: "",
};

const SOURCE_TONE = {
  hq_direct: "bg-purple-50 text-purple-800 border-purple-200",
  branch: "bg-emerald-50 text-emerald-800 border-emerald-200",
  franchise: "bg-blue-50 text-blue-800 border-blue-200",
  online: "bg-amber-50 text-amber-800 border-amber-200",
};

const PAGE_SIZE = 50;

export default function Customers() {
  const nav = useNavigate();
  const [view, setView] = useState({ mode: "list" });
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  const load = useCallback(async () => {
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (search) params.search = search;
      if (sourceFilter) params.source = sourceFilter;
      if (typeFilter) params.customer_type = typeFilter;
      const r = await api.get("/sales/customers", { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setTotalPages(r.data.total_pages || 1);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  }, [search, sourceFilter, typeFilter, page]);
  useEffect(() => { if (view.mode === "list") load(); }, [view.mode, load]);
  useEffect(() => { setPage(1); }, [search, sourceFilter, typeFilter]);

  const remove = async (c) => {
    if (!window.confirm(`Deactivate "${c.name}"?`)) return;
    try { await api.delete(`/sales/customers/${c.id}`); toast.success("Deactivated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (view.mode !== "list") return <CustomerEditor id={view.id} onBack={() => setView({ mode: "list" })} />;

  return (
    <div className="space-y-4">
      <PageSection title="Customers" description="HQ, branch, franchise, and online-source customers. Price level drives auto-fill on invoice line rates." action={
        <Button className="bg-[#9B1C1C] hover:bg-[#7F1D1D] gap-1.5" onClick={() => setView({ mode: "new" })} data-testid="cus-new-btn">
          <Plus className="h-4 w-4" /> New Customer
        </Button>
      }>
        <Surface padded={false}>
          <div className="p-3 border-b border-gray-100 flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 max-w-sm min-w-[220px]">
              <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name / mobile / GSTIN / city..." className="pl-8" data-testid="cus-search" />
            </div>
            <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} className="h-9 rounded-md border border-gray-200 text-sm px-2" data-testid="cus-source-filter">
              <option value="">All sources</option>
              <option value="hq_direct">HQ Direct</option>
              <option value="branch">Branch</option>
              <option value="franchise">Franchise</option>
              <option value="online">Online</option>
            </select>
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="h-9 rounded-md border border-gray-200 text-sm px-2" data-testid="cus-type-filter">
              <option value="">All types</option>
              <option value="retail">Retail</option>
              <option value="wholesale">Wholesale</option>
            </select>
            <span className="text-xs text-gray-500 ml-auto">{total.toLocaleString()} customer(s)</span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
                <th className="px-3 py-2">Code</th>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Source</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Mobile</th>
                <th className="px-3 py-2">City</th>
                <th className="px-3 py-2">GSTIN</th>
                <th className="px-3 py-2 text-right">Opening</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.length === 0 && (
                <tr><td colSpan={9} className="px-3 py-10 text-center text-gray-400">
                  <UserCircle className="h-8 w-8 mx-auto mb-2 opacity-30" /> No customers.
                </td></tr>
              )}
              {items.map((c) => (
                <tr key={c.id} className="hover:bg-gray-50" data-testid={`cus-row-${c.id}`}>
                  <td className="px-3 py-2 font-mono text-xs text-gray-700">{c.code || "—"}</td>
                  <td className="px-3 py-2">
                    <div className="font-medium">{c.name}</div>
                    {c.branch_name && <div className="text-[11px] text-gray-500">via {c.branch_code} · {c.branch_name}</div>}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] uppercase tracking-widest font-semibold border rounded px-2 py-0.5 ${SOURCE_TONE[c.source]}`}>{c.source.replace("_", " ")}</span>
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-700">{c.customer_type} · L{c.price_level}</td>
                  <td className="px-3 py-2 font-mono text-xs">{c.mobile || "—"}</td>
                  <td className="px-3 py-2 text-xs text-gray-600">{c.city || "—"}</td>
                  <td className="px-3 py-2 font-mono text-xs text-gray-600">{c.gstin || "—"}</td>
                  <td className="px-3 py-2 text-right text-xs tabular-nums">{c.opening_balance ? formatINR(c.opening_balance) : "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <button className="h-7 w-7 rounded hover:bg-blue-50 inline-flex items-center justify-center mr-1" onClick={() => nav(`/sales/customers/${c.id}/ledger`)} title="Ledger" data-testid={`cus-ledger-${c.id}`}>
                      <ScrollText className="h-3.5 w-3.5 text-blue-600" />
                    </button>
                    <button className="h-7 w-7 rounded hover:bg-gray-100 inline-flex items-center justify-center" onClick={() => setView({ mode: "edit", id: c.id })} data-testid={`cus-edit-${c.id}`}>
                      <Pencil className="h-3.5 w-3.5 text-gray-500" />
                    </button>
                    <button className="h-7 w-7 rounded hover:bg-red-50 inline-flex items-center justify-center ml-1" onClick={() => remove(c)} data-testid={`cus-delete-${c.id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-red-500" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Surface>
        {totalPages > 1 && (
          <div className="mt-3 bg-white border border-gray-200 rounded-md px-4 py-2.5 flex items-center gap-2 text-xs" data-testid="cus-pagination">
            <span>Page <span className="font-semibold">{page}</span> of {totalPages}</span>
            <div className="ml-auto flex gap-1">
              <Button size="sm" variant="outline" className="h-8 px-2" disabled={page <= 1} onClick={() => setPage(1)}>« First</Button>
              <Button size="sm" variant="outline" className="h-8 px-2" disabled={page <= 1} onClick={() => setPage(page - 1)}><ChevronLeft className="h-4 w-4" /></Button>
              <Button size="sm" variant="outline" className="h-8 px-2" disabled={page >= totalPages} onClick={() => setPage(page + 1)}><ChevronRight className="h-4 w-4" /></Button>
              <Button size="sm" variant="outline" className="h-8 px-2" disabled={page >= totalPages} onClick={() => setPage(totalPages)}>Last »</Button>
            </div>
          </div>
        )}
      </PageSection>
    </div>
  );
}

function CustomerEditor({ id, onBack }) {
  const isNew = !id;
  const [form, setForm] = useState(EMPTY);
  const [branches, setBranches] = useState([]);
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pinLoading, setPinLoading] = useState(false);
  const [localities, setLocalities] = useState([]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/sales/branches").then((r) => setBranches(r.data || []));
    api.get("/sales/business-settings").then((r) => setSettings(r.data || {}));
  }, []);

  useEffect(() => {
    if (!id) return;
    api.get(`/sales/customers/${id}`).then((r) => setForm({ ...EMPTY, ...r.data, opening_balance_at: r.data.opening_balance_at || EMPTY.opening_balance_at }));
  }, [id]);

  const eligibleBranches = branches.filter((b) => b.branch_type === form.source);
  const needsBranch = form.source === "branch" || form.source === "franchise";

  const lookupPin = async () => {
    if (!/^\d{6}$/.test(form.pincode || "")) return;
    setPinLoading(true);
    try {
      const r = await api.get(`/sales/pincode/${form.pincode}`);
      setLocalities(r.data.localities || []);
      setForm((f) => ({
        ...f, district: r.data.district || f.district, state: r.data.state || f.state,
        city: f.city || r.data.localities?.[0]?.name || "",
      }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Pincode lookup failed"); }
    finally { setPinLoading(false); }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (needsBranch && !form.branch_id) return toast.error(`Please pick a ${form.source}`);
    setSaving(true);
    try {
      const payload = {
        ...form,
        code: (form.code || "").trim() || null,
        opening_balance: Number(form.opening_balance) || 0,
        branch_id: needsBranch ? form.branch_id : null,
      };
      if (isNew) await api.post("/sales/customers", payload);
      else await api.put(`/sales/customers/${id}`, payload);
      toast.success("Customer saved");
      onBack();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="sm" onClick={onBack} className="gap-1.5" data-testid="cus-back"><ArrowLeft className="h-4 w-4" /> Back</Button>
        <h2 className="font-heading font-bold text-lg text-gray-900">{isNew ? "New Customer" : `Edit ${form.name || ""}`}</h2>
        <div className="ml-auto">
          <Button className="bg-[#9B1C1C] hover:bg-[#7F1D1D] gap-1.5" onClick={save} disabled={saving} data-testid="cus-save">
            <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      <Surface>
        <h3 className="font-heading font-semibold text-sm mb-3 pb-2 border-b border-gray-100">Basic</h3>
        <div className="grid grid-cols-3 gap-4">
          <F label="Code (auto if blank)">
            <Input value={form.code} onChange={(e) => set("code", e.target.value.toUpperCase())} placeholder={isNew ? "Auto-generated" : ""} className="font-mono" data-testid="cus-code" />
          </F>
          <F label="Name *" className="col-span-2"><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="cus-name" /></F>
          <F label="Type">
            <div className="flex gap-2">
              {["retail", "wholesale"].map((t) => (
                <button key={t} onClick={() => set("customer_type", t)} className={`flex-1 h-9 rounded-md border text-sm font-semibold capitalize ${form.customer_type === t ? "border-[#9B1C1C] bg-[#9B1C1C]/5 text-[#9B1C1C]" : "border-gray-200 hover:bg-gray-50"}`} data-testid={`cus-type-${t}`}>{t}</button>
              ))}
            </div>
          </F>
          <F label="Source *">
            <select value={form.source} onChange={(e) => { set("source", e.target.value); if (e.target.value === "hq_direct" || e.target.value === "online") set("branch_id", ""); }} className="h-9 w-full rounded-md border border-gray-200 text-sm px-2" data-testid="cus-source">
              <option value="hq_direct">HQ Direct</option>
              <option value="branch">Branch</option>
              <option value="franchise">Franchise</option>
              <option value="online">Online</option>
            </select>
          </F>
          {needsBranch && (
            <F label={`${form.source === "branch" ? "Branch" : "Franchise"} *`}>
              <select value={form.branch_id} onChange={(e) => set("branch_id", e.target.value)} className="h-9 w-full rounded-md border border-gray-200 text-sm px-2" data-testid="cus-branch">
                <option value="">— Select —</option>
                {eligibleBranches.map((b) => <option key={b.id} value={b.id}>{b.code} · {b.name}</option>)}
              </select>
            </F>
          )}
          <F label="Price Level">
            <select value={form.price_level} onChange={(e) => set("price_level", Number(e.target.value))} className="h-9 w-full rounded-md border border-gray-200 text-sm px-2" data-testid="cus-price-level">
              <option value={1}>Level 1 ({settings?.price_level_1_name || "MRP"})</option>
              <option value={2}>Level 2 ({settings?.price_level_2_name || "Retail"})</option>
              <option value={3}>Level 3 ({settings?.price_level_3_name || "Wholesale"})</option>
              <option value={4}>Level 4 ({settings?.price_level_4_name || "Special"})</option>
            </select>
          </F>
        </div>
      </Surface>

      <Surface>
        <h3 className="font-heading font-semibold text-sm mb-3 pb-2 border-b border-gray-100">Contact</h3>
        <div className="grid grid-cols-3 gap-4">
          <F label="Mobile 1"><Input value={form.mobile} onChange={(e) => set("mobile", e.target.value)} data-testid="cus-mobile" /></F>
          <F label="Mobile 2"><Input value={form.mobile2} onChange={(e) => set("mobile2", e.target.value)} data-testid="cus-mobile2" /></F>
          <F label="Email"><Input value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="cus-email" /></F>
        </div>
      </Surface>

      <Surface>
        <h3 className="font-heading font-semibold text-sm mb-3 pb-2 border-b border-gray-100">Address</h3>
        <div className="grid grid-cols-3 gap-4">
          <F label="Address Line 1" className="col-span-3"><Input value={form.address_line1} onChange={(e) => set("address_line1", e.target.value)} data-testid="cus-addr1" /></F>
          <F label="Address Line 2" className="col-span-3"><Input value={form.address_line2} onChange={(e) => set("address_line2", e.target.value)} data-testid="cus-addr2" /></F>
          <F label="Pincode">
            <div className="flex gap-2">
              <Input value={form.pincode} onChange={(e) => set("pincode", e.target.value.replace(/[^\d]/g, "").slice(0, 6))} onBlur={lookupPin} className="font-mono" data-testid="cus-pincode" />
              <Button variant="outline" size="sm" onClick={lookupPin} disabled={pinLoading || form.pincode?.length !== 6}>{pinLoading ? "..." : "Fetch"}</Button>
            </div>
          </F>
          <F label="City">
            {localities.length > 1 ? (
              <select value={form.city} onChange={(e) => set("city", e.target.value)} className="h-9 w-full rounded-md border border-gray-200 text-sm px-2" data-testid="cus-city-select">
                <option value="">Pick locality…</option>
                {localities.map((l) => <option key={l.name} value={l.name}>{l.name}</option>)}
              </select>
            ) : (
              <Input value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="cus-city" />
            )}
          </F>
          <F label="District"><Input value={form.district} onChange={(e) => set("district", e.target.value)} data-testid="cus-district" /></F>
          <F label="State"><Input value={form.state} onChange={(e) => set("state", e.target.value)} data-testid="cus-state" /></F>
        </div>
      </Surface>

      <Surface>
        <h3 className="font-heading font-semibold text-sm mb-3 pb-2 border-b border-gray-100">Statutory & Ledger</h3>
        <div className="grid grid-cols-3 gap-4">
          <F label="GSTIN (optional)"><Input value={form.gstin} onChange={(e) => set("gstin", e.target.value.toUpperCase())} className="font-mono" data-testid="cus-gstin" /></F>
          <F label="PAN (optional)"><Input value={form.pan} onChange={(e) => set("pan", e.target.value.toUpperCase())} className="font-mono" data-testid="cus-pan" /></F>
          <F label="Opening Balance (₹)"><Input type="number" step="0.01" value={form.opening_balance} onChange={(e) => set("opening_balance", e.target.value)} className="text-right tabular-nums" data-testid="cus-opening-bal" /></F>
          <F label="As-of Date"><Input type="date" value={form.opening_balance_at} onChange={(e) => set("opening_balance_at", e.target.value)} data-testid="cus-opening-bal-at" /></F>
          <F label="Notes" className="col-span-2"><Input value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="cus-notes" /></F>
        </div>
      </Surface>
    </div>
  );
}
